import React from 'react';
import DebugOnly from 'util/dev-tools/DebugOnly';
import RenderCounter from 'util/dev-tools/RenderCounter';

import { PdfGridsArea, PdfH1, PdfH2, PdfP, PdfPage } from './Styles.styled';
import usePcrV2 from 'components/v2/compiler/hooks/usePcrV2';
import styled from 'styled-components';

const OwnershipPage: React.FunctionComponent<{
  cpcrNumber?: string;
}> = ({ cpcrNumber }) => {

  const isPcrVersion2 = usePcrV2();

  return (
    <PdfPage>
      <DebugOnly>
        <RenderCounter componentName="Ownership page" threshold={3} />
      </DebugOnly>
      <PdfGridsArea>
        <PdfH1Version $isVersionTwo={isPcrVersion2}>Ownership and limitations on use of EPD</PdfH1Version>
        <div>
          <PdfH2>Limitations</PdfH2>
          {isPcrVersion2 && (
            <PdfP>
              EPDs within the same product category but published in different EPD programmes, may not be comparable. For two EPDs to be comparable,
              they shall be based on the same PCR (including the same first-digit version number) or be based on fully aligned PCRs or versions of PCRs;
              cover products with identical functions, technical performances and use (e.g. identical declared/functional units); have identical scope in terms
              of included life-cycle stages (unless the excluded life-cycle stage is demonstrated to be insignificant); apply identical impact assessment
              methods (including the same version of characterisation factors); and be valid at the time of comparison.
            </PdfP>
          )}
          {!isPcrVersion2 && (
            <PdfP>
              EPDs within the same product category but registered in different EPD programmes may not be comparable. For two
              EPDs to be comparable, they shall be based on the same PCR (including the same version number up to the first two
              digits) or be based on fully-aligned PCRs or versions of PCRs; cover products with identical functions, technical
              performances and use (e.g. identical declared/functional units); have equivalent system boundaries and
              descriptions of data; apply equivalent data quality requirements, methods of data collection, and allocation
              methods; apply identical cut-off rules and impact assessment methods (including the same version of
              characterisation factors); have equivalent content declarations; and be valid at the time of comparison.
            </PdfP>
          )}
          {cpcrNumber?.toLocaleLowerCase()?.includes('c-pcr-008') && (
            <>
              <PdfP>
                Additional limitations according to c-PCR: Comparability between EPDs based on this c-PCR-008 (to PCR
                2019:14) and EPDs based on PCR 2015:05 is not conceivable and shall be avoided. Any comparability of this
                kind shall be considered as false and misleading the EPD user.
              </PdfP>
              <PdfP>
                Additional limitations according to c-PCR: Comparability between EPDs based on this c-PCR-008 (to PCR
                2019:14) is only achievable, if the following performance characteristics are equivalent: Functional unit,
                Reference Service Lifetime, Usage Category, travel height, number of stops, rated load, rated speed and
                geographic region.
              </PdfP>
            </>
          )}
          {cpcrNumber?.toLocaleLowerCase()?.includes('c-pcr-010') && (
            <PdfP>
              Additional limitations according to c-PCR: Comparability between EPDs is only achievable if the following
              performance characteristics are equivalent: declared unit, containment level, level of working width, assumed
              service life, geographic region and fulfilment of the same requirements of the applicable standard (EN 1317,
              NCHRP 350 or MASH).
            </PdfP>
          )}
          {cpcrNumber?.toLocaleLowerCase()?.includes('c-pcr-014') && (
            <PdfP>
              Additional limitations according to c-PCR: Comparability between EPDs is only achievable if the following
              performance characteristics are equivalent: declared unit, containment level, level of working width, assumed
              service life, geographic region and fulfilment of the same requirements of the applicable standard (EN
              13964:2014).
            </PdfP>
          )}
          {cpcrNumber?.toLocaleLowerCase()?.includes('c-pcr-015') && (
            <PdfP>
              Additional limitations according to c-PCR: Comparability between EPDs is only achievable if the following
              performance characteristics are equivalent: declared unit, containment level, level of working width, assumed
              service life, geographic region and fulfilment of the same requirements of the applicable standard.
            </PdfP>
          )}
          {cpcrNumber?.toLocaleLowerCase()?.includes('c-pcr-025') && (
            <PdfP>
              Additional limitations according to c-PCR: Comparability between EPDs based on c-PCR Escalators and moving
              walks (to PCR 2019:14) is only achievable, if the following performance characteristics apply: functional unit
              (FU), operation mode and usage class (UC) are identical and the geographic region is equivalent.
            </PdfP>
          )}
        </div>
        <div>
          <PdfH2>Ownership</PdfH2>
          <PdfP>The EPD Owner has the sole ownership, liability, and responsibility for the EPD.</PdfP>
        </div>
      </PdfGridsArea>
    </PdfPage>
  );
};

const PdfH1Version = styled(PdfH1) <{ $isVersionTwo?: boolean }>`
  text-transform: ${(props: { $isVersionTwo?: boolean }) => (props.$isVersionTwo ? 'none' : 'uppercase')};
`;

export default OwnershipPage;